<template>
  <div class="hashtags-dashboard">
    <div
      class="dashboard-header"
      @click="toggleExpanded"
    >
      <div class="header-left">
        <i class="fa fa-hashtag header-icon" />
        <span class="header-title">Actividad por hashtag</span>
        <span
          v-if="dashboard"
          class="header-period"
        >
          {{ formattedPeriod }}
        </span>
      </div>
      <div class="header-right">
        <b-spinner
          v-if="loading"
          small
          variant="secondary"
          class="mr-2"
        />
        <i :class="['fa', expanded ? 'fa-chevron-up' : 'fa-chevron-down', 'toggle-icon']" />
      </div>
    </div>

    <transition name="slide">
      <div
        v-if="expanded"
        class="dashboard-body"
      >
        <template v-if="loading">
          <div class="hashtag-scroll">
            <div
              v-for="n in 5"
              :key="n"
              class="hashtag-card-skeleton shimmer"
            />
          </div>
        </template>

        <div
          v-else-if="!dashboard || dashboard.hashtags.length === 0"
          class="empty-state"
        >
          <i class="fa fa-hashtag mr-1" />
          Sin hashtags registrados en el período
        </div>

        <div
          v-else
          class="hashtag-scroll"
        >
          <div
            v-for="hashtag in dashboard.hashtags"
            :key="hashtag.id"
            :class="['hashtag-card', { 'is-selected': hashtag.id === selectedHashtagId }]"
            :style="{
              '--tag-color': hashtag.color,
              '--tag-bg': hashtag.color + '18',
              borderLeftColor: hashtag.color
            }"
            @click="selectHashtag(hashtag)"
          >
            <div class="card-top">
              <span
                class="card-count"
                :style="{ color: hashtag.color }"
              >{{ hashtag.totalOccurrences }}</span>
              <span class="card-label">#{{ hashtag.label }}</span>
            </div>
            <div
              v-if="hashtag.hasTrend"
              class="card-chart"
            >
              <apexchart
                type="area"
                :height="38"
                :options="getChartOptions(hashtag)"
                :series="[{ name: hashtag.label, data: hashtag.trendData }]"
              />
            </div>
            <div
              v-else
              class="card-no-trend"
            />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from '@vue/composition-api'
import { getHashtagDashboard } from '../Services'
import HashtagDashboard from '../models/HashtagDashboard'

export default {
  name: 'HashtagsDashboard',
  props: {
    selectedHashtagId: {
      type: Number,
      default: null
    },
    refreshTrigger: {
      type: Number,
      default: 0
    }
  },
  setup (props, context) {
    const loading = ref(false)
    const expanded = ref(true)
    const dashboard = ref(null)

    const formattedPeriod = computed(() => {
      if (!dashboard.value) return ''
      const fmt = (d) => {
        const [y, m, day] = d.split('-')
        return `${day}/${m}/${y}`
      }
      return `${fmt(dashboard.value.startDate)} – ${fmt(dashboard.value.endDate)}`
    })

    const toggleExpanded = () => {
      expanded.value = !expanded.value
    }

    const fetchDashboard = async () => {
      loading.value = true
      try {
        const raw = await getHashtagDashboard()
        dashboard.value = new HashtagDashboard(raw)
      } catch (e) {
        dashboard.value = null
      } finally {
        loading.value = false
      }
    }

    const getChartOptions = (hashtag) => ({
      chart: {
        type: 'area',
        sparkline: { enabled: true },
        height: 38,
        animations: { enabled: false },
        toolbar: { show: false }
      },
      stroke: { curve: 'smooth', width: 1.5 },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.35,
          opacityTo: 0,
          stops: [0, 100]
        }
      },
      colors: [hashtag.color],
      xaxis: { categories: hashtag.trendCategories },
      tooltip: {
        enabled: true,
        fixed: { enabled: false },
        x: {
          formatter: (val, opts) => {
            const cat = hashtag.trendCategories[opts.dataPointIndex]
            if (!cat) return ''
            const [, m, d] = cat.split('-')
            return `${d}/${m}`
          }
        },
        y: { title: { formatter: () => '' } }
      },
      grid: { show: false },
      markers: { size: 0 }
    })

    const selectHashtag = (hashtag) => {
      const isDeselecting = hashtag.id === props.selectedHashtagId
      context.emit('filter', isDeselecting ? null : hashtag)
    }

    watch(() => props.refreshTrigger, (val, oldVal) => {
      if (val !== oldVal) fetchDashboard()
    })

    onMounted(fetchDashboard)

    return {
      loading,
      expanded,
      dashboard,
      formattedPeriod,
      toggleExpanded,
      getChartOptions,
      selectHashtag
    }
  }
}
</script>

<style scoped>
.hashtags-dashboard {
  background: white;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  margin-bottom: 1rem;
  overflow: hidden;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.7rem 1.1rem;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;
}

.dashboard-header:hover {
  background: #f8fafc;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.header-icon {
  color: #667eea;
  font-size: 0.82rem;
}

.header-title {
  font-size: 0.82rem;
  font-weight: 600;
  color: #2d3748;
}

.header-period {
  font-size: 0.72rem;
  color: #a0aec0;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  padding: 0.1rem 0.5rem;
  border-radius: 10px;
}

.header-right {
  display: flex;
  align-items: center;
}

.toggle-icon {
  font-size: 0.72rem;
  color: #a0aec0;
}

.dashboard-body {
  padding: 0 0.75rem 0.75rem;
}

.hashtag-scroll {
  display: flex;
  gap: 0.6rem;
  overflow-x: auto;
  padding-bottom: 0.25rem;
  scrollbar-width: thin;
  scrollbar-color: #e2e8f0 transparent;
}

.hashtag-scroll::-webkit-scrollbar {
  height: 3px;
}

.hashtag-scroll::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 2px;
}

.hashtag-card {
  flex: 0 0 auto;
  width: 120px;
  background: #fafafa;
  border: 1px solid #e2e8f0;
  border-left: 3px solid var(--tag-color, #4299e1);
  border-radius: 8px;
  padding: 0.55rem 0.6rem 0.3rem;
  cursor: pointer;
  transition: box-shadow 0.15s, background 0.15s, border-color 0.15s;
}

.hashtag-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.hashtag-card.is-selected {
  background: var(--tag-bg, #eef2ff);
  border-color: var(--tag-color, #4299e1);
  border-left-width: 3px;
  box-shadow: 0 0 0 1.5px var(--tag-color, #4299e1);
}

.card-top {
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
  margin-bottom: 0.15rem;
}

.card-count {
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1;
}

.card-label {
  font-size: 0.7rem;
  color: #718096;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 70px;
}

.card-chart {
  margin: 0 -2px;
  line-height: 0;
}

.card-no-trend {
  height: 38px;
}

.hashtag-card-skeleton {
  flex: 0 0 auto;
  width: 120px;
  height: 90px;
  border-radius: 8px;
}

.empty-state {
  font-size: 0.78rem;
  color: #a0aec0;
  padding: 0.75rem 0.25rem;
}

/* Slide transition */
.slide-enter-active,
.slide-leave-active {
  transition: max-height 0.25s ease, opacity 0.2s ease;
  max-height: 200px;
  overflow: hidden;
}

.slide-enter,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
}

@keyframes shimmer {
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
}

.shimmer {
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: shimmer 1.5s infinite;
}
</style>



// WEBPACK FOOTER //
// src/app/news_feed/components/HashtagsDashboard.vue